<template>
    <div class="mb-6" flat v-if="hasAdditionalInformationRequests">
        <div class="pa-0">
            <div v-for="(request, index) in displayedRequests" :key="request.id || index"
                class="request-wrapper mb-2">
                <!-- Expandable version -->
                <v-expansion-panels v-if="expandable" flat v-model="expandedPanels[index]" class="request-panel">
                    <v-expansion-panel>
                        <v-expansion-panel-header class="request-card-header">
                            <div class="request-card-content">
                                <h3 class="request-title">
                                    {{ $t('dueDiligence.additionalInformationRequested') }}
                                    <span class="request-date">({{ formatDate(request.createdAt) }})</span>
                                </h3>
                                <p class="request-description">
                                    {{ request.description || $t('dueDiligence.noDescriptionProvided') }}
                                </p>
                            </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content class="supplier-updates-content">
                            <div v-if="!showOnlyLatest && request.farmChanges && hasFarmChanges(request.farmChanges)">
                                <h4 class="supplier-updates-title">
                                    {{ $t('dueDiligence.supplierUpdates') }}
                                    <span class="supplier-updates-date">
                                        ({{ formatDate(request.farmChanges.sessionStartAt || request.updatedAt) }})
                                    </span>
                                </h4>

                                <p class="supplier-updates-description">
                                    {{ $t('dueDiligence.supplierHasRespondedAndMadeUpdates') }}
                                </p>

                                <div class="farm-changes-links">
                                    <a v-if="request.farmChanges.removed && request.farmChanges.removed.length > 0"
                                        @click.prevent="showFarmDetails(request.farmChanges.removed, 'removed')"
                                        class="farm-change-link">
                                        {{ $t('dueDiligence.removedFarms') }} ({{ request.farmChanges.removed.length }})
                                    </a>

                                    <a v-if="request.farmChanges.updated && request.farmChanges.updated.length > 0"
                                        @click.prevent="showFarmDetails(request.farmChanges.updated, 'updated')"
                                        class="farm-change-link">
                                        {{ $t('dueDiligence.updatedFarms') }} ({{ request.farmChanges.updated.length }})
                                    </a>

                                    <a v-if="request.farmChanges.added && request.farmChanges.added.length > 0"
                                        @click.prevent="showFarmDetails(request.farmChanges.added, 'added')"
                                        class="farm-change-link">
                                        {{ $t('dueDiligence.addedNewFarms') }} ({{ request.farmChanges.added.length }})
                                    </a>
                                </div>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
                
                <!-- Non-expandable version (for FinalReport) -->
                <div v-else class="request-panel-non-expandable">
                    <div class="request-card-header">
                        <div class="request-card-content">
                            <h3 class="request-title">
                                {{ $t('dueDiligence.additionalInformationRequested') }}
                                <span class="request-date">({{ formatDate(request.createdAt) }})</span>
                            </h3>
                            <p class="request-description">
                                {{ request.description || $t('dueDiligence.noDescriptionProvided') }}
                            </p>
                        </div>
                    </div>
                    
                    <div class="supplier-updates-content" v-if="!showOnlyLatest && request.farmChanges && hasFarmChanges(request.farmChanges)">
                        <h4 class="supplier-updates-title">
                            {{ $t('dueDiligence.supplierUpdates') }}
                            <span class="supplier-updates-date">
                                ({{ formatDate(request.farmChanges.sessionStartAt || request.updatedAt) }})
                            </span>
                        </h4>

                        <p class="supplier-updates-description">
                            {{ $t('dueDiligence.supplierHasRespondedAndMadeUpdates') }}
                        </p>

                        <div class="farm-changes-links">
                            <a v-if="request.farmChanges.removed && request.farmChanges.removed.length > 0"
                                @click.prevent="showFarmDetails(request.farmChanges.removed, 'removed')"
                                class="farm-change-link">
                                {{ $t('dueDiligence.removedFarms') }} ({{ request.farmChanges.removed.length }})
                            </a>

                            <a v-if="request.farmChanges.updated && request.farmChanges.updated.length > 0"
                                @click.prevent="showFarmDetails(request.farmChanges.updated, 'updated')"
                                class="farm-change-link">
                                {{ $t('dueDiligence.updatedFarms') }} ({{ request.farmChanges.updated.length }})
                            </a>

                            <a v-if="request.farmChanges.added && request.farmChanges.added.length > 0"
                                @click.prevent="showFarmDetails(request.farmChanges.added, 'added')"
                                class="farm-change-link">
                                {{ $t('dueDiligence.addedNewFarms') }} ({{ request.farmChanges.added.length }})
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Farm Details Dialog -->
        <FarmChangesTable :dialog="showFarmDialog" :farms="selectedFarms" :type="selectedFarmType"
            :diligenceId="diligenceId" @close="showFarmDialog = false" />
    </div>
</template>

<script>
import DeforestationService from "@/_services/DeforestationService";
import FarmChangesTable from "./FarmChangesTable.vue";
import LoadingMixin from "@/mixins/LoadingMixin";

export default {
    mixins: [LoadingMixin],
    name: 'AdditionalInformationChangesLog',
    components: {
        FarmChangesTable
    },
    props: {
        diligenceId: {
            type: [Number, String],
            required: true
        },
        showOnlyLatest: {
            type: Boolean,
            default: false
        },
        expandable: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            additionalInfoRequests: [],
            farmChanges: null,
            showFarmDialog: false,
            selectedFarms: [],
            selectedFarmType: '',
            expandedPanels: {}
        };
    },
    computed: {
        hasAdditionalInformationRequests() {
            return this.additionalInfoRequests.length > 0;
        },
        displayedRequests() {
            if (this.showOnlyLatest) {
                return this.additionalInfoRequests.slice(0, 1);
            }
            return this.additionalInfoRequests;
        },
        dialogTitle() {
            const typeMap = {
                'removed': this.$t('dueDiligence.farmsRemoved'),
                'updated': this.$t('dueDiligence.farmsUpdated'),
                'added': this.$t('dueDiligence.farmsAdded')
            };
            return typeMap[this.selectedFarmType] || '';
        }
    },
    watch: {
        diligenceId: {
            immediate: true,
            handler(newId) {
                if (newId) {
                    this.loadAdditionalInformationRequests();
                }
            }
        }
    },
    methods: {
        async loadAdditionalInformationRequests() {
            if (!this.diligenceId) return;

            try {
                this.startLoading();

                // Get additional information requests
                const reportResponse = await DeforestationService.getDiligenceDetail(parseInt(this.diligenceId));
                const requests = reportResponse?.data?.getDiligenceDetail?.requestAdditionalInformation || [];

                // Get farm changes (this returns changes since the last request)
                const sortedRequests = [...requests].sort((a, b) => {
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });
                const lastRequest = sortedRequests.length > 0 ? sortedRequests[0] : null;
                const sessionStartAt = lastRequest ? lastRequest.createdAt : null;

                let farmChanges = null;
                try {
                    const changesResponse = await DeforestationService.getAdditionalInfoFarmChanges(
                        this.diligenceId, 
                        sessionStartAt
                    );
                    farmChanges = changesResponse?.data?.getAdditionalInfoFarmChanges || null;
                } catch (error) {
                    console.error('Error loading farm changes:', error);
                }

                // Map requests and attach farm changes to the most recent request

                // Attach farm changes to the most recent request (if any)
                this.additionalInfoRequests = sortedRequests.map((request, index) => {
                    // Only attach farm changes to the most recent request
                    return {
                        ...request,
                        farmChanges: index === 0 ? farmChanges : null
                    };
                });

                // Expand the first panel by default when expandable
                if (this.expandable) {
                    this.$nextTick(() => {
                        if (this.additionalInfoRequests.length > 0) {
                            this.$set(this.expandedPanels, 0, 0);
                        }
                    });
                }
            } catch (error) {
                console.error('Error loading additional information requests:', error);
                this.additionalInfoRequests = [];
            } finally {
                this.stopLoading();
            }
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        },
        hasFarmChanges(farmChanges) {
            if (!farmChanges) return false;
            const hasRemoved = farmChanges.removed && farmChanges.removed.length > 0;
            const hasUpdated = farmChanges.updated && farmChanges.updated.length > 0;
            const hasAdded = farmChanges.added && farmChanges.added.length > 0;
            return hasRemoved || hasUpdated || hasAdded;
        },
        showFarmDetails(farms, type) {
            this.selectedFarms = farms || [];
            this.selectedFarmType = type;
            this.showFarmDialog = true;
        },
        async refreshAdditionalInformationRequests() {
            await this.loadAdditionalInformationRequests();
        }
    }
};
</script>

<style lang="scss" scoped>
.request-wrapper {
    position: relative;
    margin-top: 20px;
}

.request-panel {
    ::v-deep .v-expansion-panel {
        background-color: #FFF8E1;
        border: 2px solid #FFB74D;
        border-radius: 8px;
        overflow: hidden;

        &::before {
            box-shadow: none;
        }
    }

    ::v-deep .v-expansion-panel-header {
        padding: 24px;
        min-height: auto;
        background-color: #FFF8E1;

        .v-expansion-panel-header__icon {
            margin-left: 16px;

            .v-icon {
                color: #424242;
            }
        }
    }

    ::v-deep .v-expansion-panel-content__wrap {
        padding: 0 24px 24px 24px;
    }
}

.request-panel-non-expandable {
    background-color: #FFF8E1;
    border: 2px solid #FFB74D;
    border-radius: 8px;
    overflow: hidden;
    
    .request-card-header {
        padding: 24px;
        background-color: #FFF8E1;
    }
    
    .supplier-updates-content {
        padding: 0 24px 24px 24px;
    }
}

.request-card-content {
    width: 100%;
    padding-top: 12px;
}

.request-title {
    font-size: 18px;
    font-weight: 700;
    color: #000000;
    margin: 0 0 12px 0;
}

.request-date {
    font-weight: 400;
    margin-left: 8px;
}

.request-description {
    font-size: 14px;
    line-height: 1.5;
    color: #424242;
    margin: 0;
}

.supplier-updates-content {
    background-color: #FFF8E1;
}

.supplier-updates-title {
    font-size: 16px;
    font-weight: 700;
    color: #000000;
    margin: 0 0 8px 0;
}

.supplier-updates-date {
    font-weight: 400;
    margin-left: 8px;
}

.supplier-updates-description {
    font-size: 14px;
    line-height: 1.5;
    color: #424242;
    margin: 0 0 16px 0;
}

.farm-changes-links {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.farm-change-link {
    color: #1976D2;
    text-decoration: underline;
    cursor: pointer;
    font-size: 14px;
    font-weight: 400;
    display: inline-block;
    width: fit-content;
    transition: color 0.2s;

    &:hover {
        color: #1565C0;
    }

    &:active {
        color: #0D47A1;
    }
}
</style>
